/**
 * FastPay Webhook Schemas
 *
 * Raw webhook payloads from FastPay provider.
 * Used by: payment-service/src/webhooks/fastpay.ts
 *
 * Endpoints:
 *   POST /fastpay/callback        - Deposit webhook
 *   POST /fastpay-tjs/callback    - FastPay TJS deposit webhook
 *   POST /fastpay/payout-callback - Payout webhook
 *
 * Validation: body.from === "fastpaygate"
 */
import { z } from 'zod';
// =============================================================================
// FASTPAY WEBHOOK BODY
// Source: payment-service/src/webhooks/fastpay.ts:35-43
// =============================================================================
/**
 * FastPay webhook payload (deposit and payout)
 *
 * Example:
 * {
 *   from: "fastpaygate",
 *   payment_id: "123-psp-321",
 *   status: "approve" | "cancel",
 *   amount: 5000,
 *   amount_usdt: 98.23657,
 *   type: "SBP",
 *   rate: 50.89754
 * }
 */
export const fastpayWebhookBodySchema = z.object({
    /** Source identifier - must be "fastpaygate" */
    from: z.string().optional(),
    /** Provider's transaction ID */
    payment_id: z.string().optional(),
    /** Raw status: "approve", "cancel", "check" */
    status: z.string().optional(),
    /** Amount in local currency */
    amount: z.number().optional(),
    /** Amount in USDT */
    amount_usdt: z.number().optional(),
    /** Payment type: "SBP", "C2C", "TPay" */
    type: z.string().optional(),
    /** Exchange rate */
    rate: z.number().optional(),
}).passthrough();
// =============================================================================
// VALIDATION HELPERS
// =============================================================================
/**
 * Validates FastPay webhook source
 */
export function isValidFastPaySource(body) {
    return body.from === 'fastpaygate';
}
/**
 * Checks if required fields are present
 */
export function hasFastPayRequiredFields(body) {
    return !!(body.payment_id && body.status);
}
// =============================================================================
// STATUS MAPPING
// =============================================================================
/**
 * Maps FastPay raw status to normalized WebhookStatus
 * - approve → completed
 * - cancel → cancelled
 * - check → pending (payout verification in progress)
 * - default → pending
 */
export function mapFastPayStatus(status) {
    switch (status?.toLowerCase()) {
        case 'approve':
            return 'completed';
        case 'cancel':
            return 'cancelled';
        case 'check':
            return 'pending';
        default:
            return 'pending';
    }
}
//# sourceMappingURL=fastpay.js.map